import React, { useEffect, useState } from "react";
import { frontendErrorsFilters, type FrontendErrorsFilters, type FrontendErrorsRangeChoice } from "@/features/observability/components/frontend-errors-filters";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsRange } from "@/features/observability/hooks/frontend-errors-range";
import type { FrontendErrorsSource } from "@/features/observability/types/frontend-errors.types";

type Props = {
    filters: FrontendErrorsFilters;
    range: FrontendErrorsRange | null;
    retentionFloor: number;
    onChange: (next: FrontendErrorsFilters) => void;
};

const RANGE_OPTIONS: { value: FrontendErrorsRangeChoice; label: string }[] = [
    { value: "1h", label: "1 h" },
    { value: "24h", label: "24 h" },
    { value: "7d", label: "7 d" },
    { value: "custom", label: "Rango libre" },
];

const SOURCES: FrontendErrorsSource[] = ["frontend", "backend"];

const pad = (value: number): string => String(value).padStart(2, "0");

const toLocalInput = (ms: number | null): string => {
    if (ms === null) return "";
    const date = new Date(ms);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromLocalInput = (value: string): number | null => {
    if (value === "") return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
};

type DimensionProps = {
    label: string;
    placeholder: string;
    value: string | null;
    onCommit: (value: string | null) => void;
};

// Committed on blur or Enter, not per keystroke: every commit is a new query.
function DimensionInput({ label, placeholder, value, onCommit }: DimensionProps) {
    const [draft, setDraft] = useState(value === null ? "" : value);

    useEffect(() => {
        setDraft(value === null ? "" : value);
    }, [value]);

    const commit = (): void => {
        const next = frontendErrorsFilters.toDimension(draft);
        if (next !== value) onCommit(next);
    };

    return (
        <label className="fe-field">
            {label}
            <input
                className="fe-input"
                value={draft}
                placeholder={placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === "Enter") commit();
                }}
            />
        </label>
    );
}

export function FrontendErrorsFilterBar({ filters, range, retentionFloor, onChange }: Props) {
    const choose = (choice: FrontendErrorsRangeChoice): void => {
        if (choice === "custom") {
            onChange({
                ...filters,
                range: "custom",
                customFrom: range === null ? filters.customFrom : range.from,
                customTo: range === null ? filters.customTo : range.to,
            });
            return;
        }
        onChange({ ...filters, range: choice, customFrom: null, customTo: null });
    };

    return (
        <div className="fe-filters">
            <label className="fe-field">
                Workspace
                <select
                    className="fe-select"
                    value={filters.workspaceId}
                    onChange={(event) => onChange({ ...filters, workspaceId: event.target.value })}
                >
                    {frontendErrorsFilters.SUPPORTED_WORKSPACES.map((workspace) => (
                        <option key={workspace} value={workspace}>{workspace}</option>
                    ))}
                </select>
            </label>

            <div className="fe-field">
                Rango temporal
                <div className="fe-field__row">
                    <div className="fe-seg" role="group" aria-label="Rango temporal">
                        {RANGE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className="fe-seg__btn"
                                aria-pressed={filters.range === option.value}
                                onClick={() => choose(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {filters.range === "custom" ? (
                        <div className="fe-field__row">
                            <input
                                type="datetime-local"
                                className="fe-input fe-input--date"
                                aria-label="Desde"
                                min={toLocalInput(retentionFloor)}
                                value={toLocalInput(filters.customFrom)}
                                onChange={(event) => onChange({ ...filters, customFrom: fromLocalInput(event.target.value) })}
                            />
                            <span className="fe-muted">→</span>
                            <input
                                type="datetime-local"
                                className="fe-input fe-input--date"
                                aria-label="Hasta"
                                min={toLocalInput(retentionFloor)}
                                value={toLocalInput(filters.customTo)}
                                onChange={(event) => onChange({ ...filters, customTo: fromLocalInput(event.target.value) })}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="fe-field">
                Origen
                <div className="fe-seg" role="group" aria-label="Origen">
                    {SOURCES.map((source) => (
                        <button
                            key={source}
                            type="button"
                            className="fe-seg__btn"
                            aria-pressed={filters.source === source}
                            onClick={() => onChange({ ...filters, source })}
                        >
                            {source}
                        </button>
                    ))}
                </div>
            </div>

            <label className="fe-field">
                Categoría
                <select
                    className="fe-select fe-select--wide"
                    value={filters.category === null ? "all" : filters.category}
                    onChange={(event) => {
                        const value = event.target.value;
                        onChange({ ...filters, category: frontendErrorsFormat.isCategory(value) ? value : null });
                    }}
                >
                    <option value="all">Todas</option>
                    {frontendErrorsFormat.CATEGORIES.map((category) => (
                        <option key={category} value={category}>{frontendErrorsFormat.categoryStyle(category).label}</option>
                    ))}
                </select>
            </label>

            <DimensionInput
                label="Código de error"
                placeholder="UNKNOWN_HTTP"
                value={filters.errorCode}
                onCommit={(errorCode) => onChange({ ...filters, errorCode })}
            />
            <DimensionInput
                label="Release"
                placeholder="a1b2c3d4e5f6"
                value={filters.release}
                onCommit={(release) => onChange({ ...filters, release })}
            />
        </div>
    );
}
